import React from "react";
import {
  Button,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Loader,
  LoaderSizes,
  Pagination,
  SearchInput,
  Tag,
  TagAppearance,
} from "@hid-galaxy-ui/galaxy-react";
import { useLocation, useNavigate } from "react-router";
import {
  getAllPartnersSagaAction,
  getFilteredPartnersSagaAction,
} from "../../../../redux/saga/partner-details/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import moment from "moment";
import { PartnerDetailType } from "../../../../redux/slices/partner-details/Types";
import PartnerFilterModal from "./partner-filter-modal";
import ExportReportModal from "./export-report-modal";
import "./PartnerList.scss";
import FeatureFormModal from "./feature-form-modal";
import IncidentFormModal from "./incident-form-modal";
import IncidentList from "./incident-list";
import FeatureList from "./feature-list";

const DashboardList = () => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(20);
  const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false);
  const [exportModalOpen, setExportModalOpen] = React.useState<boolean>(false);
  const [addIncidentOpen, setAddIncidentOpen] = React.useState<boolean>(false);
  const [addFeatureOpen, setAddFeatureOpen] = React.useState<boolean>(false);
  const [showIncidentList, setShowIncidentList] =
    React.useState<boolean>(true);
  const [showFeatureList, setShowFeatureList] = React.useState<boolean>(false);
  const [incidentCount, setIncidentCount] = React.useState<number>(0);
  const [featureCount, setFeatureCount] = React.useState<number>(0);
  const [partnerFilter, setPartnerFilter] = React.useState<{
    partnerName: string[];
    status: string[];
    type: string[];
    tier: string[];
    vertical: string[];
    revenueStream: string[];
    accountManager: string[];
    region: string[];
    country: string[];
    contractName: string[];
    contractStatus: string[];
    startDateRange: string[];
    endDateRange: string[];
  }>({
    partnerName: [],
    status: [],
    type: [],
    tier: [],
    vertical: [],
    revenueStream: [],
    accountManager: [],
    region: [],
    country: [],
    contractName: [],
    contractStatus: [],
    startDateRange: [],
    endDateRange: [],
  });
  const { allPartners, filteredPartners, status } = useAppSelector(
    (state) => state.partner_details
  );
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  React.useEffect(() => {
    dispatch(getAllPartnersSagaAction());

    if (location.state) {
      if (location.state.organizationStatusFilter) {
        const statusFilter = location.state.organizationStatusFilter;
        setPartnerFilter((prev) => ({
          ...prev,
          status: [statusFilter],
        }));
      }
    }
  }, []);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm]);

  React.useEffect(() => {
    const hasFilters = Object.values(partnerFilter).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (hasFilters) {
      dispatch(
        getFilteredPartnersSagaAction({
          partner_status: partnerFilter.status.join(","),
          partner_name: partnerFilter.partnerName.join(","),
          partner_type: partnerFilter.type.join(","),
          partner_tier: partnerFilter.tier.join(","),
          partner_vertical: partnerFilter.vertical.join(","),
          revenue_stream: partnerFilter.revenueStream.join(","),
          account_manager: partnerFilter.accountManager.join(","),
          region: partnerFilter.region.join(","),
          country: partnerFilter.country.join(","),
          contract_name: partnerFilter.contractName.join(","),
          contract_status: partnerFilter.contractStatus.join(","),
          start_date_range: partnerFilter.startDateRange,
          end_date_range: partnerFilter.endDateRange,
        })
      );
    }
    setCurrentPageNumber(1);
  }, [partnerFilter]);

  const statusCounts = React.useMemo(() => {
    return allPartners.reduce<Record<PartnerDetailType["status"], number>>(
      (acc, partner) => {
        acc[partner.status] = (acc[partner.status] || 0) + 1;
        return acc;
      },
      { Active: 0, Registered: 0, Lapsed: 0, Inactive: 0 }
    );
  }, [allPartners]);

  const tiles = [
    {
      id: "active",
      label: "Feature Management",
      value: featureCount,
      color: "#94BB20",
      onClick: () => {
        setShowFeatureList(true);
        setShowIncidentList(false);
      },
    },
    // {
    //   id: "registered",
    //   label: "Incident Management",
    //   value: incidentCount,
    //   color: "#00AEEF",
    //   onClick: () => {
    //     setShowIncidentList(true);
    //     setShowFeatureList(false);
    //   },
    // },
    // {
    //   id: "lapsed",
    //   label: "Health Checkups",
    //   value: statusCounts.Lapsed,
    //   color: "#F8971D",
    //   onClick: () =>
    //     setPartnerFilter((prev) => ({
    //       ...prev,
    //       status: ["Lapsed"],
    //     })),
    // },
  ];

  const hasActiveFilters = Object.values(partnerFilter).some(
    (arr) => Array.isArray(arr) && arr.length > 0
  );

  const partnerList = hasActiveFilters ? filteredPartners : allPartners;

  const filteredPartnersLocal = partnerList.filter((partner) => {
    const matchesSearch =
      searchTerm.length === 0 ||
      partner.partner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.partner_id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const totalRecords = filteredPartnersLocal.length;

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const paginatedData = filteredPartnersLocal.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  // Load feature count from sessionStorage
  React.useEffect(() => {
    const loadFeatureCount = () => {
      const storedFeatures = sessionStorage.getItem("feature_details");
      if (storedFeatures) {
        try {
          const features = JSON.parse(storedFeatures);
          setFeatureCount(Array.isArray(features) ? features.length : 0);
        } catch (error) {
          setFeatureCount(0);
        }
      } else {
        setFeatureCount(0);
      }
    };

    loadFeatureCount();

    // Listen for feature updates
    const handleFeaturesUpdate = () => loadFeatureCount();
    window.addEventListener("featuresUpdated", handleFeaturesUpdate);

    return () => {
      window.removeEventListener("featuresUpdated", handleFeaturesUpdate);
    };
  }, []);

  // Load incident count from localStorage
  React.useEffect(() => {
    const loadIncidentCount = () => {
      const storedIncidents = localStorage.getItem("incidents");
      if (storedIncidents) {
        try {
          const incidents = JSON.parse(storedIncidents);
          setIncidentCount(Array.isArray(incidents) ? incidents.length : 0);
        } catch (error) {
          setIncidentCount(0);
        }
      } else {
        setIncidentCount(0);
      }
    };

    loadIncidentCount();

    // Listen for incident updates
    const handleIncidentsUpdate = () => loadIncidentCount();
    window.addEventListener("incidentsUpdated", handleIncidentsUpdate);

    return () => {
      window.removeEventListener("incidentsUpdated", handleIncidentsUpdate);
    };
  }, []);

  const partnerStatus = (status: string) => {
    switch (status) {
      case "Active":
        return (
          <InlineStatus
            label="Active"
            appearance={InlineStatusAppearance.Positive}
            iconType={InlineStatusIconType.Dot}
          />
        );
      case "Lapsed":
        return (
          <InlineStatus
            label="Lapsed"
            appearance={InlineStatusAppearance.Caution}
            iconType={InlineStatusIconType.Dot}
          />
        );
      case "Inactive":
        return (
          <InlineStatus
            label="Inactive"
            appearance={InlineStatusAppearance.Danger}
            iconType={InlineStatusIconType.Dot}
          />
        );
      default:
        return (
          <InlineStatus
            label="Registered"
            appearance={InlineStatusAppearance.Info}
            iconType={InlineStatusIconType.Dot}
          />
        );
    }
  };

  const onAddFeatureClick = () => {
    console.log("Feature button clicked - opening modal");
    setAddFeatureOpen(true);
  };

  const onAddIncidentClick = () => {
    console.log("Incident button clicked - opening modal");
    setAddIncidentOpen(true);
  };

  return (
    <div className="partner-list-container">
      {/* <div className="search-table-container">
        <div className="search-input-filter-wrapper">
          <div className="left">
            <h1>Feature Dashboard</h1>
          </div>
          <div className="right"> */}
      {/* <Button
          variant="primary"
          label="View Feature"
          // icon={<Iconography icon="plus" />}
          onClick={() => navigate("/hid-partner-admin/dashboard/feature-management")}
        /> */}
      {/* </div>
        </div>
      </div> */}

      {/* <div className="tiles-container">
        {tiles.map((tile) => {
          return (
            <div className="tile" key={tile.id} onClick={() => tile.onClick()}>
              <div className="label">{tile.label}</div>
              <div className="value-circle">
                <div className="value">{tile.value}</div>
                <div
                  className="circle-out"
                  style={{ backgroundColor: `${tile.color}47` }}
                >
                  <div
                    className="circle-in"
                    style={{ backgroundColor: tile.color }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div> */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",

        }}
        className="tiles-container"
      >
        <Button
          variant="primary"
          label="Add New Feature"
          icon={<Iconography icon="userPlus" size={IconographySizes.Medium} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddFeatureClick();
          }}
        />
        <Button
          variant="primary"
          label="Add New  Incident"
          icon={<Iconography icon="userPlus" size={IconographySizes.Medium} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddIncidentClick();
          }}
        />
      </div> */}
      {/* Show Feature List when Feature Management card is clicked */}
      {showFeatureList && (
        <div style={{ marginTop: "10px" }}>
          <FeatureList />
        </div>
      )}

      {/* Show Incident List when Incident Management card is clicked */}
      {showIncidentList && (
        <div style={{ marginTop: "10px" }}>
          <IncidentList />
        </div>
      )}

      {/* <div className="search-table-container">
        <div className="search-input-filter-wrapper">
          <div className="left">
            <SearchInput
              theme="dark"
              id="searchPartner"
              name="searchPartner"
              placeholder="Search Partner by Name or ID"
              isDisplayClearIcon={true}
              value={searchTerm}
              onChange={(event: any) =>
                setSearchTerm(event.currentTarget.value)
              }
              onSearch={(event, value) => {
                setSearchTerm(value);
              }}
            />
            {hasActiveFilters ? (
              <Button
                variant="primary"
                icon={
                  <Iconography icon="sliders" size={IconographySizes.Medium} />
                }
                onClick={() => setFilterModalOpen(true)}
              />
            ) : (
              <Button
                variant="tertiary"
                icon={
                  <Iconography icon="sliders" size={IconographySizes.Medium} />
                }
                onClick={() => setFilterModalOpen(true)}
              />
            )}
          </div>
          <div className="right">
            <Button
              variant="secondary"
              label="Export"
              icon={<Iconography icon="arrowUpFromSquare" />}
              onClick={() => setExportModalOpen(true)}
            />
          </div>
        </div>
        {hasActiveFilters && (
          <div className="filter-tag-wrapper">
            {Object.entries(partnerFilter).map(([filterKey, values]) => {
              if (filterKey === "startDateRange" && values.length) {
                return (
                  <Tag
                    value={`Start Date: ${values[0]} - ${values[1]}`}
                    appearance={TagAppearance.Blue}
                    isCloseIcon={true}
                    key={filterKey}
                    onCloseClick={() =>
                      setPartnerFilter((prev) => ({
                        ...prev,
                        [filterKey]: [],
                      }))
                    }
                  />
                );
              } else if (filterKey === "endDateRange" && values.length) {
                return (
                  <Tag
                    value={`End Date: ${values[0]} - ${values[1]}`}
                    appearance={TagAppearance.Blue}
                    isCloseIcon={true}
                    key={filterKey}
                    onCloseClick={() =>
                      setPartnerFilter((prev) => ({
                        ...prev,
                        [filterKey]: [],
                      }))
                    }
                  />
                );
              } else {
                return values.map((value) => {
                  let label = value;
                  switch (filterKey) {
                    case "contractStatus":
                      label = `Contract: ${value}`;
                      break;
                    case "tier":
                      label = `Tier: ${value}`;
                      break;
                    default:
                      label = value;
                      break;
                  }
                  return (
                    <Tag
                      value={label}
                      appearance={TagAppearance.Blue}
                      isCloseIcon={true}
                      key={`${filterKey}-${value}`}
                      onCloseClick={() =>
                        setPartnerFilter((prev) => ({
                          ...prev,
                          [filterKey]: prev[
                            filterKey as keyof typeof prev
                          ].filter((item) => item !== value),
                        }))
                      }
                    />
                  );
                });
              }
            })}
          </div>
        )}
        {status.allPartnersLoading || status.filteredPartnersLoading ? (
          <div className="list-loading">
            <Loader variant={LoaderSizes.large} />
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-header">
                <div className="col-1">Partner Name</div>
                <div className="col-2">Organization Status</div>
                <div className="col-3">HID Account Manager</div>
                <div className="col-4">Region</div>
                <div className="col-5">Country</div>
                <div className="col-6">Last Updated</div>
              </div>

              {paginatedData.length > 0 ? (
                paginatedData.map((partner, index) => {
                  return (
                    <div
                      className="table-row"
                      key={partner.partner_id}
                      style={index === 0 ? { border: "none" } : {}}
                      onClick={() =>
                        navigate(
                          `/hid-admin/manage-partner/${partner.ID}/partner-details`
                        )
                      }
                    >
                      <div className="col-1">
                        <span>{partner.partner_name}</span>
                      </div>
                      <div className="col-2">
                        {partnerStatus(partner.status)}
                      </div>
                      <div className="col-3">{partner.account_manager}</div>
                      <div className="col-4">{partner.region}</div>
                      <div className="col-5">{partner.country}</div>
                      <div className="col-6">
                        {moment(partner.last_updated).format("DD MMM, YYYY")}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-data">No data available for display.</div>
              )}
            </div>
            <Pagination
              currentPageNumber={currentPageNumber}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageNumberClick={handlePageChange}
              onPageSizesClick={onPageSizesClick}
              pageSizes={[
                { label: "20", value: "20" },
                { label: "50", value: "50" },
                { label: "100", value: "100" },
              ]}
            />
          </>
        )}
      </div> */}
      {filterModalOpen && (
        <PartnerFilterModal
          partnerFilterProp={partnerFilter}
          setPartnerFilterProp={setPartnerFilter}
          setModalOpen={setFilterModalOpen}
        />
      )}
      {exportModalOpen && (
        <ExportReportModal
          partnerFilterProp={partnerFilter}
          setModalOpen={setExportModalOpen}
        />
      )}

      {addFeatureOpen && (
        <FeatureFormModal setModalOpen={setAddFeatureOpen} mode="Add" />
      )}
      {addIncidentOpen && (
        <IncidentFormModal setModalOpen={setAddIncidentOpen} mode="Add" />
      )}
    </div>
  );
};

export default DashboardList;
